import { Pressable, StyleSheet, View } from "react-native";
import { useRouter } from "expo-router";

import { AppText, Card, ProtectedScreen, Stack } from "~/components/ui";
import { useNotifications } from "~/hooks/useNotifications";
import {
  radii,
  spacing,
  useThemedStyles,
  type ThemeColors,
} from "~/styles/theme";

function formatNotificationDate(value: string) {
  return new Intl.DateTimeFormat("en-NZ", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

function notificationKindLabel(kind: string) {
  if (kind === "workspace") return "Workspace";
  if (kind === "invoice") return "Invoice";
  if (kind === "subscription") return "Subscription";
  return "MintStint";
}

export default function NotificationsScreen() {
  const stylesheet = useThemedStyles(makeStyles);
  const router = useRouter();
  const { notifications, error, loading, markRead } = useNotifications();

  return (
    <ProtectedScreen
      title="Notifications"
      subtitle="Updates, invoice reminders, and account notices."
    >
      {loading ? <AppText>Loading notifications...</AppText> : null}
      {error ? <AppText tone="danger">{error.message}</AppText> : null}
      {!loading && notifications.length === 0 ? (
        <Card gap="sm">
          <AppText weight="semibold">No notifications yet</AppText>
          <AppText size="xs">
            Invoice reminders and MintStint account notices will appear here.
          </AppText>
        </Card>
      ) : null}
      <Stack gap="sm">
        {notifications.map((notification) => (
          <Pressable
            key={notification.id}
            accessibilityRole="link"
            onPress={() => {
              void markRead(notification.id);
              router.push(`/notifications/${notification.id}` as never);
            }}
            style={({ hovered, pressed }) => [
              stylesheet.item,
              !notification.readAt ? stylesheet.unread : null,
              hovered || pressed ? stylesheet.hover : null,
            ]}
          >
            <View style={stylesheet.itemHeader}>
              <AppText weight="semibold">{notification.title}</AppText>
              {!notification.readAt ? (
                <View style={stylesheet.unreadDot} />
              ) : null}
            </View>
            <AppText size="xs">{notification.body}</AppText>
            <View style={stylesheet.metaRow}>
              <AppText size="xs">
                {notificationKindLabel(notification.kind)}
              </AppText>
              <AppText size="xs">
                {formatNotificationDate(notification.created_at)}
              </AppText>
            </View>
          </Pressable>
        ))}
      </Stack>
    </ProtectedScreen>
  );
}

const makeStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    item: {
      gap: spacing.xs,
      padding: spacing.m,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: radii.l,
      backgroundColor: colors.background,
    },
    unread: { borderColor: colors.primary, backgroundColor: colors.surface },
    hover: { backgroundColor: colors.mutedSurface },
    itemHeader: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      gap: spacing.s,
    },
    metaRow: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: spacing.s,
      justifyContent: "space-between",
    },
    unreadDot: {
      width: 8,
      height: 8,
      borderRadius: 4,
      backgroundColor: colors.primary,
    },
  });
